// frontend/src/components/BookDetail.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingButton } from '@mui/lab';
import {
    DialogActions, DialogContent, DialogTitle, Button, IconButton, TextField, Grid, Box, Autocomplete,
    Dialog, DialogContentText, Typography, Paper, CircularProgress, Snackbar, Alert,
    AppBar, Toolbar,
    Stack,
    Divider,
    // NEUE Imports für Rating-UI
    Chip,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import StopIcon from '@mui/icons-material/Stop';
import axios from 'axios';

// NEUE Imports für Rating API
import { fetchBookAPI, updateBookAPI, fetchRatingForBookAPI, rateBookAPI, borrowBookAPI, returnBookAPI, fetchCurrentLoansAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';

// Helper Komponente (bleibt gleich)
const DetailItem = ({ label, value, variant = "body1", sx = {}, actionButton = null }) => (
  <Box mb={1.5} sx={sx}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
          {label}
        </Typography>
        {actionButton}
    </Box>
    <Typography variant={variant} sx={{ whiteSpace: label === 'Beschreibung' ? 'pre-wrap' : 'normal' }}>
      {value || '-'}
    </Typography>
  </Box>
);

// NEU: Mapping für Status-Anzeige
const readingStatusLabels = {
    read: 'Gelesen',
    wants_to_read: 'Will ich lesen',
};
const keepStatusLabels = {
    keep: 'Behalten',
    discard: 'Kann weg',
};
const readingStatusColors = {
    read: 'success',
    wants_to_read: 'info',
};
const keepStatusColors = {
    keep: 'success',
    discard: 'warning',
};


function BookDetail({ bookId, onClose, isEditMode, locations, onActionComplete, isMobile }) {
  const [book, setBook] = useState(null);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [bookInfo, setBookInfo] = useState(null); // Behalten für Google Books Info
  const [selectedLocation, setSelectedLocation] = useState(null);
  const { handleApiError, user } = useAuth();
  const { userPayload } = useAuth();

  // --- NEUER State für Rating ---
  const [rating, setRating] = useState(null); // Speichert das Rating-Objekt { readingStatus, keepStatus }
  const [isFetchingRating, setIsFetchingRating] = useState(false);
  const [ratingError, setRatingError] = useState(null);
  // --- Ende NEUER State ---

  // --- Ausleihsystem ---
  const [loanInfo, setLoanInfo] = useState(null);
  const [isLoanLoading, setIsLoanLoading] = useState(false);
  const [loanError, setLoanError] = useState(null);
  const [borrowerEmail, setBorrowerEmail] = useState(''); // NEU: Leihenden erfassen

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [isSpeaking, setIsSpeaking] = useState(false);
  const utteranceRef = useRef(null);

  const fetchLoanStatus = useCallback(async () => {
    setIsLoanLoading(true);
    setLoanError(null);
    try {
      const loans = await fetchCurrentLoansAPI();
      const found = loans.find(l => l.book._id === bookId);
      setLoanInfo(found || null);
    } catch (loanErr) {
      console.error("Fehler beim Holen des Ausleihstatus:", loanErr);
      setLoanError(loanErr.message);
    } finally {
      setIsLoanLoading(false);
    }
  }, [bookId]);

  const handleBorrow = async () => {
    setIsLoanLoading(true);
    setLoanError(null);
    try {
      await borrowBookAPI(bookId, borrowerEmail); // NEU: Leihenden erfassen
      showSnackbar('Buch erfolgreich ausgeliehen.', 'success');
      fetchLoanStatus();
      if (onActionComplete) onActionComplete(); // <-- Sofort nach Verleihen aufrufen
    } catch (err) {
      setLoanError(err.message);
      showSnackbar(`Fehler beim Ausleihen: ${err.message}`, 'error');
    } finally {
      setIsLoanLoading(false);
    }
  };

  const handleReturn = async () => {
    setIsLoanLoading(true);
    setLoanError(null);
    try {
      await returnBookAPI(bookId);
      showSnackbar('Buch erfolgreich zurückgegeben.', 'success');
      fetchLoanStatus();
      if (onActionComplete) onActionComplete(); // <-- Sofort nach Rückgabe aufrufen
    } catch (err) {
      setLoanError(err.message);
      showSnackbar(`Fehler beim Zurückgeben: ${err.message}`, 'error');
    } finally {
      setIsLoanLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (window.speechSynthesis && window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
        utteranceRef.current = null;
      }
    };
  }, []);

  const fetchBook = useCallback(async () => {
    if (window.speechSynthesis && window.speechSynthesis.speaking) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
        utteranceRef.current = null;
    }
    if (!bookId) return;

    setIsFetching(true);
    setIsFetchingRating(true); // Auch Rating-Fetch starten
    setIsLoanLoading(true); // Auch Loan-Fetch starten
    setError(null);
    setRatingError(null); // Rating-Fehler zurücksetzen
    setLoanError(null); // Loan-Fehler zurücksetzen
    setBookInfo(null);
    setRating(null); // Rating zurücksetzen
    setLoanInfo(null); // Loan zurücksetzen

    try {
      // 1. Buchdaten holen
      const bookData = await fetchBookAPI(bookId);
      setBook(bookData);
      const initialLocation = locations?.find(loc => loc._id === bookData.location?._id) || null;
      setSelectedLocation(initialLocation);

      // 2. Rating-Daten holen (NEU)
      try {
          const ratingData = await fetchRatingForBookAPI(bookId);
          setRating(ratingData); // Kann null sein, wenn kein Rating existiert
          console.log("Rating fetched:", ratingData);
      } catch (ratingErr) {
          console.error("Fehler beim Holen des Ratings:", ratingErr);
          setRatingError(ratingErr.message);
          // handleApiError(ratingErr); // Optional: Auch hier Logout auslösen?
      }

      // 3. Loan-Daten holen (NEU)
      try {
          const loans = await fetchCurrentLoansAPI();
          const found = loans.find(l => l.book._id === bookId);
          setLoanInfo(found || null);
      } catch (loanErr) {
          console.error("Fehler beim Holen des Ausleihstatus:", loanErr);
          setLoanError(loanErr.message);
      }

      // 4. Google Books Info (wie im Original)
      if (bookData.isbn) {
          fetchGoogleBookInfo(bookData.isbn);
      }

    } catch (err) {
      handleApiError(err);
      setError(err.message); // Hauptfehler (Buch-Fetch)
    } finally {
      setIsFetching(false);
      setIsFetchingRating(false); // Rating-Fetch beendet
      setIsLoanLoading(false); // Loan-Fetch beendet
    }
  }, [bookId, handleApiError, locations]); // fetchGoogleBookInfo nicht als Abhängigkeit, da es nur aufgerufen wird

  useEffect(() => {
    fetchBook();
  }, [fetchBook]);

  // fetchGoogleBookInfo bleibt unverändert
  const fetchGoogleBookInfo = async (isbn) => {
      // Implementierung bleibt gleich...
      try {
          const response = await axios.get(`https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}`);
          if (response.data.items && response.data.items.length > 0) {
              const info = response.data.items[0].volumeInfo;
              setBookInfo({
                  averageRating: info.averageRating,
                  publisher: info.publisher,
                  infoLink: info.infoLink
              });
          }
      } catch (error) {
          console.error("Fehler beim Abrufen der Google Books Info:", error);
          // Kein Fehler-State setzen, da es nur Zusatzinfo ist
      }
  };

  // handleInputChange für Buchdaten bleibt unverändert
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBook(prev => ({ ...prev, [name]: value }));
  };

  // handleLocationChange bleibt unverändert
  const handleLocationChange = (e, value) => {
    setSelectedLocation(value);
  };

  // --- NEUER Handler für Rating-Änderungen im Edit-Modus ---
  const handleRatingChange = useCallback((event, newValue, type) => {
      console.log(`Rating changed: type=${type}, newValue=${newValue}`);
      setRating(prev => {
          // Initialisiere Rating-Objekt, falls es null ist
          const currentRating = prev ? { ...prev } : { readingStatus: null, keepStatus: null };
          if (type === 'readingStatus') {
              currentRating.readingStatus = newValue; // newValue kann null sein
          } else if (type === 'keepStatus') {
              currentRating.keepStatus = newValue; // newValue kann null sein
          }
          console.log("New rating state:", currentRating);
          return currentRating;
      });
  }, []);
  // --- Ende NEUER Handler ---

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedLocation) {
        showSnackbar('Bitte wähle einen Lagerort aus.', 'warning');
        return;
    }
    setIsSaving(true);
    setError(null);
    setRatingError(null);

    let bookUpdateSuccess = false;
    let ratingUpdateSuccess = false;

    try {
        // 1. Buchdaten aktualisieren (wie bisher)
        const bookUpdateData = { ...book, location: selectedLocation._id };
        // ... (restliche Bereinigung der Buchdaten) ...
        delete bookUpdateData._id;
        delete bookUpdateData.createdAt;
        delete bookUpdateData.updatedAt;
        delete bookUpdateData.isbn;
        if (typeof bookUpdateData.authors === 'string') {
            bookUpdateData.authors = bookUpdateData.authors.split(',').map(a => a.trim()).filter(a => a);
        }
        delete bookUpdateData.locationInfo; // Falls vorhanden

        await updateBookAPI(bookId, bookUpdateData);
        bookUpdateSuccess = true;
        console.log("Buchdaten erfolgreich gespeichert.");

        // 2. Rating aktualisieren (NEU: Vereinfachte Logik)
        // Rufe die API immer auf, wenn der Benutzer die Rating-Controls berührt hat (rating state ist nicht mehr null)
        // Das Backend kümmert sich darum, ob es ein Update, Insert oder Delete ist.
        if (rating !== null) { // Prüfe nur, ob das Rating-Objekt existiert (durch handleRatingChange gesetzt)
            try {
                // Sende die aktuellen Status (können null sein!)
                await rateBookAPI(bookId, rating.readingStatus, rating.keepStatus);
                ratingUpdateSuccess = true;
                console.log("Rating erfolgreich gespeichert/gelöscht.");
            } catch (ratingErr) {
                console.error("Fehler beim Speichern des Ratings:", ratingErr);
                setRatingError(ratingErr.message);
                handleApiError(ratingErr);
                // ratingUpdateSuccess bleibt false
            }
        } else {
            // Der Benutzer hat die Rating-Controls nicht berührt, nichts zu tun.
            ratingUpdateSuccess = true; // Kein Fehler
        }

        // 3. Erfolg melden und Dialog schließen
        if (bookUpdateSuccess && ratingUpdateSuccess) {
            showSnackbar('Änderungen erfolgreich gespeichert.', 'success');
            if (onActionComplete) onActionComplete(true);
            onClose();
        } else if (bookUpdateSuccess && !ratingUpdateSuccess) {
            // Buch ok, Rating fehlgeschlagen -> Zeige Rating-Fehler, Dialog bleibt offen
            showSnackbar(`Buchdaten gespeichert, aber Fehler beim Speichern der Bewertung: ${ratingError}`, 'warning');
            setIsSaving(false); // Ladeanzeige beenden
        }
        // Fall: bookUpdateSuccess = false wird im Haupt-catch behandelt

    } catch (err) {
        handleApiError(err);
        setError(err.message);
        showSnackbar(`Fehler beim Speichern der Buchdaten: ${err.message}`, 'error');
    } finally {
        // Setze isSaving immer zurück, außer bei Rating-Fehler (wo es schon passiert ist)
        if (bookUpdateSuccess && ratingUpdateSuccess) {
           setIsSaving(false);
        }
        // Bei unvollständigem Rating oder Rating-Fehler wurde isSaving bereits auf false gesetzt
    }
  };


  const showSnackbar = (message, severity = 'info') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
  };
  const handleSnackbarClose = (event, reason) => {
      if (reason === 'clickaway') { return; }
      setSnackbarOpen(false);
  };

  // handleToggleSpeech bleibt unverändert
  const handleToggleSpeech = useCallback(() => {
    if (!book?.description) return;
    if (!('speechSynthesis' in window)) {
      showSnackbar('Dein Browser unterstützt die Vorlesefunktion nicht.', 'warning');
      return;
    }
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      utteranceRef.current = null;
    } else {
      const utterance = new SpeechSynthesisUtterance(book.description);
      utterance.lang = 'de-DE';
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => { setIsSpeaking(false); utteranceRef.current = null; };
      utterance.onerror = (event) => {
        console.error('SpeechSynthesis Error:', event);
        setIsSpeaking(false); utteranceRef.current = null;
        showSnackbar(`Fehler bei der Sprachausgabe: ${event.error}`, 'error');
      };
      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    }
  }, [book?.description, isSpeaking, showSnackbar]);


  // --- Rendering ---
  // Kombinierter Ladezustand (NEU: isFetchingRating hinzugefügt)
  const isLoading = isFetching || isSaving || isFetchingRating || isLoanLoading;

  // Ladeanzeigen und Fehler (NEU: Rating-Zustände berücksichtigt)
  if (isFetching && !book) return <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />;
  if (error && !snackbarOpen) return <Typography color="error" sx={{ p: 2 }}>Fehler beim Laden des Buches: {error}</Typography>;
  // Zeige Rating-Fehler nur im View-Mode, wenn Buch geladen ist und kein anderer Fehler angezeigt wird
  if (!isEditMode && ratingError && !error && !isFetching && !snackbarOpen) return <Typography color="error" sx={{ p: 2 }}>Fehler beim Laden der Bewertung: {ratingError}</Typography>;
  if (!book) return <Typography sx={{ p: 2 }}>Buch nicht gefunden.</Typography>;

  const coverUrl = book.cachedCoverUrl || book.thumbnail?.replace(/^http:\/\//, 'https://');
  const showRealCover = coverUrl && !coverUrl.endsWith('generic-book-cover.png');

  // speechButton bleibt unverändert
  const speechButton = !isEditMode && book?.description && 'speechSynthesis' in window ? (
    <IconButton onClick={handleToggleSpeech} size="small" aria-label={isSpeaking ? "Vorlesen stoppen" : "Beschreibung vorlesen"} disabled={isLoading} sx={{ ml: 1 }}>
        {isSpeaking ? <StopIcon fontSize="inherit" /> : <VolumeUpIcon fontSize="inherit" />}
    </IconButton>
  ) : null;

  return (
    <>
      {/* Mobile Header (bleibt gleich) */}
      {isMobile && (
          <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                  <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close" disabled={isSaving}> <CloseIcon /> </IconButton>
                  <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div" noWrap> {isEditMode ? 'Buch bearbeiten' : 'Buchdetails'} </Typography>
                  {isEditMode && ( <LoadingButton color="inherit" onClick={handleSubmit} loading={isSaving} loadingPosition="start" startIcon={<SaveIcon />} disabled={isLoading} > Speichern </LoadingButton> )}
                  {!isEditMode && ( <Button color="inherit" onClick={onClose}> Schließen </Button> )}
              </Toolbar>
          </AppBar>
      )}
      {/* Desktop Title (bleibt gleich) */}
      {!isMobile && (
          <DialogTitle sx={{ m: 0, p: 2, bgcolor: 'primary.main', color: 'white' }}>
              {isEditMode ? 'Buch bearbeiten' : 'Buchdetails'}
              <IconButton aria-label="close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }} disabled={isSaving}> <CloseIcon /> </IconButton>
          </DialogTitle>
      )}

      {/* Gemeinsamer Content */}
      <DialogContent sx={isMobile ? { pt: 2, pb: 2 } : { padding: 2 }}>
        {/* NEU: Zeige Ladeanzeige für Rating, wenn Buch schon da ist */}
        {isFetchingRating && book && <CircularProgress size={20} sx={{ position: 'absolute', top: isMobile ? '70px' : '70px', right: '20px', zIndex: 1 }} />}
        {/* NEU: Zeige Rating-Fehler im Edit-Modus, wenn er auftritt (z.B. beim Speichern) */}
        {isEditMode && ratingError && <Alert severity="error" sx={{ mb: 2 }}>Bewertungsfehler: {ratingError}</Alert>}

        <Box component={isEditMode ? "form" : "div"} onSubmit={isEditMode ? handleSubmit : undefined} sx={{ opacity: isSaving ? 0.5 : 1 }}>
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Thumbnail (bleibt gleich) */}
            <Grid item xs={12} sm={4} md={3}>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', pt: 1 }}>
                {showRealCover ? ( <img src={coverUrl} alt={book.title} style={{ maxHeight: '250px', maxWidth: '100%', objectFit: 'contain', borderRadius: '4px', border: '1px solid #eee' }} /> ) : ( <Paper elevation={1} sx={{ width: 128, height: 192, display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center', bgcolor: 'grey.200' }}> <Typography variant="caption">Kein Cover verfügbar</Typography> </Paper> )}
              </Box>
            </Grid>

            {/* Felder */}
            <Grid item xs={12} sm={8} md={9}>
                {isEditMode ? (
                    // --- Bearbeitungsmodus ---
                    <Stack spacing={2}>
                        {/* Buchdaten-Felder (wie bisher) */}
                        <TextField label="Titel" value={book.title || ''} onChange={handleInputChange} name="title" disabled={isLoading} fullWidth variant="outlined" required size="small" />
                        <TextField label="Autoren (Komma-getrennt)" value={Array.isArray(book.authors) ? book.authors.join(', ') : book.authors || ''} onChange={handleInputChange} name="authors" disabled={isLoading} fullWidth variant="outlined" size="small" />
                        <Divider sx={{ my: 1 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}> <TextField label="ISBN" value={book.isbn || ''} name="isbn" disabled fullWidth variant="outlined" size="small" /> </Grid>
                            <Grid item xs={12} sm={6}> <Autocomplete id="location-select-detail" options={locations || []} getOptionLabel={(option) => option?.name || ''} isOptionEqualToValue={(option, value) => option?._id === value?._id} value={selectedLocation || null} onChange={handleLocationChange} disabled={isLoading} size="small" renderInput={(params) => ( <TextField {...params} label="Lagerort" variant="outlined" required error={!selectedLocation} /> )} /> </Grid>
                            <Grid item xs={12} sm={6}> <TextField label="Erscheinungsdatum" value={book.publishedDate || ''} onChange={handleInputChange} name="publishedDate" disabled={isLoading} fullWidth variant="outlined" size="small" /> </Grid>
                            <Grid item xs={12} sm={6}> <TextField label="Genre" value={book.genre || ''} onChange={handleInputChange} name="genre" disabled={isLoading} fullWidth variant="outlined" size="small" /> </Grid>
                            <Grid item xs={12} sm={6}> <TextField label="Seitenzahl" value={book.pageCount || ''} onChange={handleInputChange} name="pageCount" disabled={isLoading} fullWidth variant="outlined" type="number" size="small" /> </Grid>
                        </Grid>
                        <Divider sx={{ my: 1 }} />
                        <TextField label="Beschreibung" value={book.description || ''} onChange={handleInputChange} name="description" disabled={isLoading} fullWidth multiline minRows={isMobile ? 3 : 4} maxRows={8} variant="outlined" size="small" />

                        {/* --- NEU: Rating Edit Controls --- */}
                        <Divider sx={{ my: 1 }} />
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" gutterBottom>Deine Bewertung</Typography>
                            {/* Lesestatus */}
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Lesestatus</Typography>
                                <ToggleButtonGroup
                                    value={rating?.readingStatus || null} // Wert aus State holen
                                    exclusive // Nur eine Option wählbar
                                    onChange={(e, val) => handleRatingChange(e, val, 'readingStatus')}
                                    aria-label="Lesestatus"
                                    size="small"
                                    disabled={isLoading}
                                    fullWidth={isMobile} // Volle Breite auf Mobile
                                >
                                    <ToggleButton value="wants_to_read" aria-label="Will ich lesen">
                                        Will ich lesen
                                    </ToggleButton>
                                    <ToggleButton value="read" aria-label="Gelesen">
                                        Gelesen
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            {/* Behalten-Status */}
                            <Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Behalten-Status</Typography>
                                <ToggleButtonGroup
                                    value={rating?.keepStatus || null} // Wert aus State holen
                                    exclusive // Nur eine Option wählbar
                                    onChange={(e, val) => handleRatingChange(e, val, 'keepStatus')}
                                    aria-label="Behalten-Status"
                                    size="small"
                                    disabled={isLoading}
                                    fullWidth={isMobile} // Volle Breite auf Mobile
                                >
                                    <ToggleButton value="keep" aria-label="Behalten">
                                        Behalten
                                    </ToggleButton>
                                    <ToggleButton value="discard" aria-label="Kann weg">
                                        Kann weg
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Stack>
                        {/* --- Ende NEU --- */}
                    </Stack>
                ) : (
                    // --- Ansichtsmodus ---
                    <Stack spacing={0}>
                        {/* Buchdaten (wie bisher) */}
                        <DetailItem label="Titel" value={book.title} variant="h6" />
                        <DetailItem label="Autoren" value={Array.isArray(book.authors) ? book.authors.join(', ') : book.authors} variant="subtitle1" sx={{ mb: 2 }} />
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={isMobile ? 1 : 2}>
                            <Grid item xs={12} sm={6}> <DetailItem label="ISBN" value={book.isbn} /> </Grid>
                            <Grid item xs={12} sm={6}> <DetailItem label="Lagerort" value={selectedLocation?.name} /> </Grid>
                            <Grid item xs={12} sm={6}> <DetailItem label="Erscheinungsdatum" value={book.publishedDate} /> </Grid>
                            <Grid item xs={12} sm={6}> <DetailItem label="Genre" value={book.genre} /> </Grid>
                            <Grid item xs={12} sm={6}> <DetailItem label="Seitenzahl" value={book.pageCount} /> </Grid>
                        </Grid>

                        {/* --- NEU: Rating Anzeige --- */}
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Deine Bewertung</Typography>
                        {isFetchingRating ? (
                            <Typography variant="body2" color="text.secondary">Lade Bewertung...</Typography>
                        ) : ratingError ? (
                            <Typography variant="body2" color="error">Bewertung konnte nicht geladen werden.</Typography>
                        ) : rating ? (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                {rating.readingStatus && (
                                    <Chip
                                        label={readingStatusLabels[rating.readingStatus]}
                                        size="small"
                                        color={readingStatusColors[rating.readingStatus] || 'default'}
                                        variant="outlined"
                                    />
                                )}
                                {rating.keepStatus && (
                                     <Chip
                                        label={keepStatusLabels[rating.keepStatus]}
                                        size="small"
                                        color={keepStatusColors[rating.keepStatus] || 'default'}
                                        variant="outlined"
                                    />
                                )}
                                {!rating.readingStatus && !rating.keepStatus && (
                                     <Typography variant="body2" color="text.secondary">Noch nicht bewertet.</Typography>
                                )}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Noch nicht bewertet.</Typography>
                        )}
                        {/* --- Ende NEU --- */}

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Ausleihstatus</Typography>
                        {isLoanLoading ? (
                          <Typography variant="body2" color="text.secondary">Lade Ausleihstatus...</Typography>
                        ) : loanError ? (
                          <Typography variant="body2" color="error">{loanError}</Typography>
                        ) : loanInfo ? (
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip label="Ausgeliehen" color="warning" size="small" />
                            <Typography variant="body2" color="text.secondary">
                              {loanInfo.user?.email ? `von ${loanInfo.user.email}` : ''}
                              {loanInfo.loanedAt ? ` seit ${new Date(loanInfo.loanedAt).toLocaleDateString()}` : ''}
                            </Typography>
                            {/* Rückgabe-Button für alle Benutzer */}
                            <Button onClick={handleReturn} size="small" disabled={isLoanLoading} variant="outlined">Zurückgeben</Button>
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Button onClick={handleBorrow} size="small" disabled={isLoanLoading} variant="contained">Ausleihen</Button>
                            {/* Admin: Borrower-Email eingeben */}
                            {userPayload?.isAdmin && (
                              <TextField
                                label="Leihender (E-Mail)"
                                value={borrowerEmail}
                                onChange={e => setBorrowerEmail(e.target.value)}
                                size="small"
                                sx={{ minWidth: 220 }}
                                placeholder="E-Mail des Leihenden"
                                disabled={isLoanLoading}
                              />
                            )}
                          </Stack>
                        )}

                        <Divider sx={{ my: 2 }} />
                        {/* Beschreibung mit Action Button (wie bisher) */}
                        <DetailItem
                            label="Beschreibung"
                            value={book.description}
                            actionButton={speechButton}
                        />

                        {/* Google Books Info (wie bisher) */}
                        {bookInfo && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'medium' }}>Zusätzliche Infos (Google Books)</Typography>
                                    <Stack spacing={0.5}>
                                        {bookInfo.averageRating && <DetailItem label="Bewertung" value={`${bookInfo.averageRating} / 5`} variant="body2" />}
                                        {bookInfo.publisher && <DetailItem label="Verlag" value={bookInfo.publisher} variant="body2" />}
                                        {bookInfo.infoLink && (
                                            <Box>
                                                <Button size="small" href={bookInfo.infoLink} target="_blank" rel="noopener noreferrer" sx={{ p: 0, justifyContent: 'flex-start' }}>
                                                    Mehr bei Google Books
                                                </Button>
                                            </Box>
                                        )}
                                    </Stack>
                                </Paper>
                            </>
                        )}
                    </Stack>
                )}
            </Grid>
          </Grid>
          {/* Desktop Actions (bleibt gleich) */}
           {!isMobile && (
               <DialogActions sx={{ pt: 3, pr: 0 }}>
                    <Button onClick={onClose} disabled={isSaving}> {isEditMode ? 'Abbrechen' : 'Schließen'} </Button>
                    {isEditMode && ( <LoadingButton type="submit" variant="contained" color="primary" loading={isSaving} loadingPosition="start" startIcon={<SaveIcon />} disabled={isLoading} > Speichern </LoadingButton> )}
                </DialogActions>
           )}
        </Box> {/* Ende Form/Div */}
      </DialogContent>

       {/* Snackbar (bleibt gleich) */}
       <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }} variant="filled"> {snackbarMessage} </Alert>
      </Snackbar>
    </>
  );
}

export default BookDetail;
